import * as Lucide from "lucide-react";
import cn from "classnames";
import { DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { useConversationSwitching } from "@/hooks/useConversationSwitching";
import {
  useAiActionStore,
  AI_MODEL,
  AI_AGENT,
} from "@/store/zustand/aiActionStore";

interface MenuItemProps {
  value: AI_MODEL;
  agent: AI_AGENT;
  label: React.ReactNode;
  isSelected: boolean;
  disabled?: boolean;
  onClose: () => void;
  tooltip?: string;
  lockedTooltip?: string;
}

export const MenuItem = ({
  value,
  agent,
  label,
  isSelected,
  disabled = false,
  onClose,
  tooltip,
  lockedTooltip,
}: MenuItemProps) => {
  const setAgent = useAiActionStore((state) => state.setAgent);
  const setCodeGenerationModel = useAiActionStore(
    (state) => state.setCodeGenerationModel,
  );
  const { switchToModelConversation } = useConversationSwitching();

  return (
    <DropdownMenuItem
      data-testid={`model-item-${value}`}
      className={cn(
        "py-2 px-3 text-xs flex items-center justify-between cursor-pointer rounded-md transition-colors",
        "hover:bg-secondary-n-900",
        isSelected && "bg-secondary-n-950",
        disabled && "opacity-50 cursor-not-allowed",
        lockedTooltip && "cursor-not-allowed",
      )}
      onSelect={() => {
        if (disabled || lockedTooltip) return;

        setAgent(agent);
        setCodeGenerationModel(value, async () => {
          await switchToModelConversation();
        });
        onClose();
      }}
      disabled={disabled && !lockedTooltip}
      data-tooltip-id={tooltip || lockedTooltip ? "tooltip" : undefined}
      data-tooltip-content={lockedTooltip || tooltip}
      data-tooltip-place="top"
    >
      {label}
      {lockedTooltip ? (
        <Lucide.Lock className="ml-auto h-3 w-3 text-text-tertiary shrink-0" />
      ) : isSelected ? (
        <Lucide.Check className="ml-2 h-3.5 w-3.5 text-primary-n-600" />
      ) : null}
    </DropdownMenuItem>
  );
};
